/**
 * Weights and baseline settings (FR-22, FR-28, §8, §14).
 *
 * The panel exists to settle an argument, not to expose knobs. §14 leaves the
 * tiebreaker philosophy — best-player-available versus need-weighted —
 * genuinely undecided and calls the §8 weights "guesses until tested against
 * real drafts". You cannot test a guess you cannot see the effect of, so the
 * design point here is the **live top five**: it re-ranks as you drag, against
 * your actual board, so the trade-off stops being abstract.
 *
 * Two properties that make it safe to fiddle with mid-draft:
 *
 *  - **Hot-reloadable** (FR-28). Changes apply immediately, with no restart and
 *    no loss of draft state. The board behind the panel updates too.
 *  - **Revertible.** The weights as they were on open are captured, and Revert
 *    restores them. Experimenting while on the clock should not be a one-way
 *    door.
 */

import { el, clear, fmt } from './dom.ts';
import type { Store } from '../state/store.ts';
import type { BaselineMethod, EngineWeights } from '../core/types.ts';
import { DEFAULT_WEIGHTS } from '../core/types.ts';
import { BASELINE_METHODS, WEIGHT_PRESETS, matchPreset } from '../core/presets.ts';
import { normalizeWeights } from '../state/config.ts';

/**
 * Slider definitions. The help text explains the term, not the slider.
 *
 * Every feature behind these is standardised across the board before its weight
 * is applied, so these numbers are shares of influence and can be read against
 * each other: 0.50 really is about half the decision, and doubling a slider
 * really does double how much that term moves the board.
 */
const WEIGHT_FIELDS: { key: keyof EngineWeights; label: string; help: string }[] = [
  {
    key: 'vor',
    label: 'Value',
    help: 'Points above a replacement-level player at the same position. The engine’s main signal.',
  },
  {
    key: 'rank',
    label: 'Your ranking',
    help: 'Where the player sits on your active source. Turn this to 1 and everything else to 0 to follow your list exactly.',
  },
  {
    key: 'scarcity',
    label: 'Scarcity',
    help: 'How fast the position is draining relative to the board, and how thin the current tier is.',
  },
  {
    key: 'need',
    label: 'Roster fit',
    help: 'How much less a player is worth to your lineup than to an empty one — zero while you still have a slot open at his position, negative once you don’t. Raise this if the engine keeps offering you depth you cannot start.',
  },
  {
    key: 'availability',
    label: 'Don’t lose him',
    help: 'How much waiting would cost: the chance he is gone by your next pick, times how much worse your fallback at that position would be. This is what produces “don’t reach — he’ll be back to you”.',
  },
  {
    key: 'lookahead',
    label: 'Next pick',
    help: 'Looks one turn ahead: how good the follow-up is that this pick leaves you. Taking a quarterback fills your only quarterback slot, so the pick after it can only be a back or a receiver — which is what stops the board offering you five quarterbacks at once.',
  },
  {
    key: 'bye',
    label: 'Bye conflict',
    help: 'Small penalty for stacking a bye week where you would be unable to field a starter.',
  },
];

export function renderSettingsPanel(host: HTMLElement, store: Store, onClose: () => void): void {
  clear(host);

  // FR-28 applies changes immediately, so capture the entry state to revert to.
  const original = { ...store.get().config.engine.weights };
  const originalBaseline = store.get().config.engine.baseline_method;

  const preview = el('div', { class: 'settings-preview' });
  const presetRow = el('div', { class: 'filter-row' });
  const sliderHost = el('div', { class: 'settings-sliders' });
  const baselineHost = el('div', { class: 'settings-baseline' });
  const blendHost = el('div', { class: 'settings-blend' });

  const apply = (patch: { weights?: EngineWeights; baseline?: BaselineMethod }): void => {
    const engine = store.get().config.engine;
    store.patchConfig({
      engine: {
        ...engine,
        weights: patch.weights ? normalizeWeights(patch.weights) : engine.weights,
        baseline_method: patch.baseline ?? engine.baseline_method,
      },
    });
    store.recompute(true);
    renderAll();
  };

  // -------------------------------------------------------------------------
  // Live top five — the reason this panel exists
  // -------------------------------------------------------------------------

  const renderPreview = (): void => {
    clear(preview);
    const engine = store.get().engine;

    preview.appendChild(el('h3', {}, 'Top five with these weights'));

    if (!engine || engine.top.length === 0) {
      preview.appendChild(el('p', { class: 'hint' }, 'No board loaded yet.'));
      return;
    }

    const list = el('ol', { class: 'settings-top5' });
    engine.top.forEach((rec, i) => {
      list.appendChild(
        el(
          'li',
          {},
          el('span', { class: 'st5-rank' }, String(i + 1)),
          el('span', { class: 'st5-name' }, rec.player.full_name),
          el('span', { class: 'st5-score' }, rec.valuation.final_score.toFixed(3)),
          el(
            'span',
            { class: 'st5-meta' },
            `${rec.player.position ?? '—'} · ${rec.player.team ?? 'FA'}`,
          ),
        ),
      );
    });
    preview.appendChild(list);
    preview.appendChild(
      el(
        'p',
        { class: 'hint' },
        `Baseline ${engine.baselines.method}. This list and the board behind this panel both update as you drag.`,
      ),
    );
  };

  // -------------------------------------------------------------------------
  // Presets
  // -------------------------------------------------------------------------

  const renderPresets = (): void => {
    clear(presetRow);
    const current = store.get().config.engine.weights;
    const active = matchPreset(current);

    for (const preset of WEIGHT_PRESETS) {
      presetRow.appendChild(
        el(
          'button',
          {
            class: `chip ${active?.id === preset.id ? 'active' : ''}`,
            type: 'button',
            title: `${preset.description}\n\nTrade-off: ${preset.tradeoff}`,
            onClick: () => apply({ weights: preset.weights, baseline: preset.baseline }),
          },
          preset.label,
        ),
      );
    }

    // Say what the current stance costs, not just what it is. Every preset
    // gives something up, and a settings panel that only lists benefits is
    // selling rather than informing.
    const note = el('p', { class: 'settings-preset-note' });
    if (active) {
      note.appendChild(el('strong', {}, `${active.label}: `));
      note.appendChild(document.createTextNode(`${active.description} ${active.tradeoff}`));
    } else {
      note.appendChild(document.createTextNode('Hand-tuned. Pick a preset above to start from a known stance.'));
    }
    presetRow.appendChild(note);
  };

  // -------------------------------------------------------------------------
  // Sliders
  // -------------------------------------------------------------------------

  const renderSliders = (): void => {
    clear(sliderHost);
    const weights = store.get().config.engine.weights;
    const total = Object.values(weights).reduce((a, w) => a + w, 0);

    for (const field of WEIGHT_FIELDS) {
      const value = weights[field.key];
      const valueLabel = el('span', { class: 'slider-value' }, value.toFixed(2));

      const input = el('input', {
        type: 'range',
        class: 'slider',
        min: '0',
        max: '1',
        step: '0.01',
        value: String(value),
        'aria-label': field.label,
        onInput: (event: Event) => {
          const next = Number((event.target as HTMLInputElement).value);
          valueLabel.textContent = next.toFixed(2);
          apply({ weights: { ...store.get().config.engine.weights, [field.key]: next } });
        },
      });

      sliderHost.appendChild(
        el(
          'div',
          { class: 'slider-row' },
          el(
            'div',
            { class: 'slider-head' },
            el('label', { class: 'slider-label' }, field.label),
            valueLabel,
          ),
          input,
          el('p', { class: 'slider-help' }, field.help),
        ),
      );
    }

    // Every term is standardised before its weight applies, so a weight is a
    // share of influence and the ratios above are directly readable. Saying so
    // is the point: these numbers used to be multipliers on features measured
    // in different units, where "Value 0.35" quietly carried about three
    // quarters of the decision.
    sliderHost.appendChild(
      el(
        'p',
        { class: 'hint' },
        `Weights total ${total.toFixed(2)}. Each term is put on a common scale before its weight applies, so these are shares of the decision: a slider at twice another moves the board about twice as much. They need not sum to 1 — only the ratios affect the ordering — but keeping them near 1 makes scores comparable between sessions.`,
      ),
    );
  };

  // -------------------------------------------------------------------------
  // Baseline method (FR-22)
  // -------------------------------------------------------------------------

  const renderBaseline = (): void => {
    clear(baselineHost);
    const current = store.get().config.engine.baseline_method;

    baselineHost.appendChild(el('h3', {}, 'Replacement baseline'));
    for (const method of BASELINE_METHODS) {
      baselineHost.appendChild(
        el(
          'label',
          { class: `baseline-option ${current === method.id ? 'active' : ''}` },
          el('input', {
            type: 'radio',
            name: 'baseline',
            value: method.id,
            checked: current === method.id,
            onChange: () => apply({ baseline: method.id }),
          }),
          el(
            'span',
            {},
            el('strong', {}, method.label),
            el('span', { class: 'baseline-desc' }, method.description),
          ),
        ),
      );
    }
  };

  // -------------------------------------------------------------------------
  // Per-source blend weights (FR-15)
  // -------------------------------------------------------------------------

  const renderBlend = (): void => {
    clear(blendHost);
    const state = store.get();
    if (state.sources.length < 2) return;

    blendHost.appendChild(el('h3', {}, 'Blend weights'));
    blendHost.appendChild(
      el(
        'p',
        { class: 'hint' },
        'Used when the active source is "Blend". Blending happens on rank percentile, so sources of different lengths stay comparable.',
      ),
    );

    for (const source of state.sources) {
      const weight = state.config.blend_weights[source.source_id] ?? 0;
      const label = el('span', { class: 'slider-value' }, weight.toFixed(2));

      blendHost.appendChild(
        el(
          'div',
          { class: 'slider-row' },
          el(
            'div',
            { class: 'slider-head' },
            el('label', { class: 'slider-label' }, source.source_name),
            label,
          ),
          el('input', {
            type: 'range',
            class: 'slider',
            min: '0',
            max: '1',
            step: '0.05',
            value: String(weight),
            'aria-label': `${source.source_name} blend weight`,
            onInput: (event: Event) => {
              const next = Number((event.target as HTMLInputElement).value);
              label.textContent = next.toFixed(2);
              store.patchConfig({
                blend_weights: { ...store.get().config.blend_weights, [source.source_id]: next },
              });
              store.recompute(true);
              renderPreview();
            },
          }),
        ),
      );
    }
  };

  const renderAll = (): void => {
    renderPresets();
    renderSliders();
    renderBaseline();
    renderBlend();
    renderPreview();
  };

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  host.appendChild(
    el(
      'div',
      { class: 'modal settings-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Recommendation weights' },
      el(
        'header',
        { class: 'modal-head' },
        el('h2', {}, 'Recommendation settings'),
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×'),
      ),
      el(
        'p',
        { class: 'hint' },
        'Changes apply immediately — no restart, and your draft state is untouched. Revert puts everything back the way it was when you opened this.',
      ),
      presetRow,
      el(
        'div',
        { class: 'settings-body' },
        el('div', { class: 'settings-controls' }, sliderHost, baselineHost, blendHost),
        preview,
      ),
      el(
        'footer',
        { class: 'modal-foot' },
        el(
          'button',
          {
            class: 'btn btn-quiet',
            type: 'button',
            onClick: () => apply({ weights: { ...DEFAULT_WEIGHTS }, baseline: 'BEER' }),
          },
          'Reset to defaults',
        ),
        el(
          'button',
          {
            class: 'btn',
            type: 'button',
            onClick: () => apply({ weights: original, baseline: originalBaseline }),
          },
          'Revert',
        ),
        el('button', { class: 'btn btn-primary', type: 'button', onClick: onClose }, 'Done'),
      ),
    ),
  );

  renderAll();
}
